import React from 'react';
import {
  Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ModuleColumn } from '../../types';
import { brandColors, RADIUS, APP_FONT_FAMILY } from '../../theme/appTheme';
import { tableActionButtonSx } from '../../theme/formStyles';
import {
  buildExportColumns,
  downloadImportTemplate,
  exportRowsToExcel,
  normalizeImportRows,
  parseImportFile,
} from '../../utils/exportToExcel';

interface TableImportExportMenusProps {
  columns: ModuleColumn[];
  exportExtraColumns?: { id: string; header: string; exportValue?: (row: Record<string, unknown>) => unknown; searchValue?: (row: Record<string, unknown>) => string }[];
  selectedRows: Record<string, unknown>[];
  filteredRows: Record<string, unknown>[];
  canImport?: boolean;
  canExport?: boolean;
  enableExcelExport?: boolean;
  onImport?: (records: Record<string, unknown>[]) => void | Promise<void>;
  onServerExport?: () => void;
  exportFileName?: string;
  exportSheetName?: string;
  loading?: boolean;
}

export const TableImportExportMenus: React.FC<TableImportExportMenusProps> = ({
  columns,
  exportExtraColumns = [],
  selectedRows,
  filteredRows,
  canImport,
  canExport,
  enableExcelExport,
  onImport,
  onServerExport,
  exportFileName = 'export',
  exportSheetName = 'Data',
  loading,
}) => {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [importAnchor, setImportAnchor] = React.useState<null | HTMLElement>(null);
  const [exportAnchor, setExportAnchor] = React.useState<null | HTMLElement>(null);

  const exportCols = React.useMemo(() => {
    const cols = columns
      .filter((c) => c.field !== '_actions')
      .map((c) => ({
        id: c.field,
        header: c.header,
        exportValue: c.exportValue,
        searchValue: c.searchValue,
      }));
    exportExtraColumns.forEach((c) => {
      if (!cols.some((x) => x.id === c.id)) {
        cols.push({
          id: c.id,
          header: c.header,
          exportValue: c.exportValue,
          searchValue: c.searchValue,
        });
      }
    });
    return cols;
  }, [columns, exportExtraColumns]);

  const templateCols = React.useMemo(() => {
    const cols = columns
      .filter((c) => c.field !== '_actions')
      .map((c) => ({ id: c.field, header: c.header }));
    exportExtraColumns.forEach((c) => {
      if (!cols.some((x) => x.id === c.id)) {
        cols.push({ id: c.id, header: c.header });
      }
    });
    return cols;
  }, [columns, exportExtraColumns]);

  const showImport = Boolean(canImport && onImport);
  const showExport = Boolean(canExport || enableExcelExport || onServerExport);

  const runExcelExport = (rows: Record<string, unknown>[], suffix: string) => {
    if (!rows.length) return;
    exportRowsToExcel({
      columns: buildExportColumns(exportCols),
      rows,
      fileName: `${exportFileName}-${suffix}`,
      sheetName: exportSheetName,
    });
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImport) return;
    try {
      const raw = await parseImportFile(file);
      const normalized = normalizeImportRows(raw, templateCols.map((c) => ({ field: c.id, header: c.header })));
      await onImport(normalized);
    } finally {
      e.target.value = '';
    }
  };

  const menuBtnSx = {
    ...tableActionButtonSx,
  };

  if (!showImport && !showExport) return null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {showImport && (
        <>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={handleImportFile} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />}
            onClick={(e) => setImportAnchor(e.currentTarget)}
            sx={{
              ...menuBtnSx,
              height: 32,
              minHeight: 32,
              borderColor: brandColors.border,
              color: brandColors.textDark,
              bgcolor: '#fff',
            }}
          >
            Import
          </Button>
          <Menu
            anchorEl={importAnchor}
            open={Boolean(importAnchor)}
            onClose={() => setImportAnchor(null)}
            PaperProps={{ sx: { borderRadius: `${RADIUS}px`, fontFamily: APP_FONT_FAMILY } }}
          >
            <MenuItem onClick={() => { setImportAnchor(null); fileRef.current?.click(); }}>
              <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Import file" secondary="CSV or Excel (.xlsx)" />
            </MenuItem>
            <MenuItem onClick={() => {
              setImportAnchor(null);
              downloadImportTemplate({ columns: templateCols, fileName: exportFileName, sheetName: 'Template' });
            }}>
              <ListItemIcon><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Download template" secondary="Column headers for import" />
            </MenuItem>
          </Menu>
        </>
      )}

      {showExport && (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={loading}
            sx={{
              ...menuBtnSx,
              height: 32,
              minHeight: 32,
              borderColor: brandColors.border,
              color: brandColors.textDark,
              bgcolor: '#fff',
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            PaperProps={{ sx: { borderRadius: `${RADIUS}px`, fontFamily: APP_FONT_FAMILY } }}
          >
            {(enableExcelExport || canExport) && (
              <MenuItem
                disabled={!selectedRows.length}
                onClick={() => { setExportAnchor(null); runExcelExport(selectedRows, 'selected'); }}
              >
                <ListItemIcon><FileDownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={`Export selected (${selectedRows.length})`}
                  secondary="Excel — checked rows only"
                />
              </MenuItem>
            )}
            {(enableExcelExport || canExport) && (
              <MenuItem
                disabled={!filteredRows.length}
                onClick={() => { setExportAnchor(null); runExcelExport(filteredRows, 'all'); }}
              >
                <ListItemIcon><FileDownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={`Export all (${filteredRows.length})`}
                  secondary="Excel — current filtered list"
                />
              </MenuItem>
            )}
            {onServerExport && (
              <>
                {(enableExcelExport || canExport) && <Divider />}
                <MenuItem onClick={() => { setExportAnchor(null); onServerExport(); }}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Export all (server CSV)" secondary="Full module export from server" />
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};
